import { QuoteItem } from '../types';
import { isQuoteItemExcluded } from './quoteLogic';

/**
 * Multi-select and bulk striking of quote positions (QuoteEditor position table).
 *
 * Why a separate module: the editor is a thin container — selection state,
 * per-section selection and the bulk `excluded` switch are plain data
 * transformations and must stay testable without a DOM environment (the
 * project has no jsdom). The component only holds the Set and renders.
 *
 * `excluded` itself is owned by quoteLogic (isQuoteItemExcluded); nothing here
 * re-implements the "is this position active" rule.
 */

/** Minimal shape the helpers need — keeps them usable with test fixtures. */
export type SelectableQuoteItem = Pick<QuoteItem, 'id' | 'excluded'>;

/** Selection state of one section header checkbox. */
export type SectionSelectionState = 'none' | 'some' | 'all';

/** Counters behind the bulk action bar. */
export interface QuoteSelectionStats {
    /** Selected positions that still exist in the list. */
    total: number;
    /** …of those, how many are already struck. */
    excluded: number;
    /** …and how many are not. */
    active: number;
}

const emptySelection: ReadonlySet<string> = new Set<string>();

/** Add/remove one position. Returns a new Set — never mutates the argument. */
export const toggleQuoteItemSelection = (selection: ReadonlySet<string>, id: string): Set<string> => {
    const next = new Set(selection);
    if (!next.delete(id)) next.add(id);
    return next;
};

/**
 * Section header checkbox: select every position of the section, or clear them
 * all again once the section is fully selected. Positions of other sections
 * keep their state.
 */
export const toggleQuoteSectionSelection = (
    selection: ReadonlySet<string>,
    sectionItemIds: readonly string[],
): Set<string> => {
    const next = new Set(selection);
    const full = sectionSelectionState(selection, sectionItemIds) === 'all';
    sectionItemIds.forEach((id) => { if (full) next.delete(id); else next.add(id); });
    return next;
};

/** Drives checked/indeterminate of the section header checkbox. */
export const sectionSelectionState = (
    selection: ReadonlySet<string>,
    sectionItemIds: readonly string[],
): SectionSelectionState => {
    if (!sectionItemIds.length) return 'none';
    const hit = sectionItemIds.filter((id) => selection.has(id)).length;
    if (hit === 0) return 'none';
    return hit === sectionItemIds.length ? 'all' : 'some';
};

/**
 * Ids that no longer exist (deleted position) are dropped. Keeps the bulk bar
 * from counting ghosts after a delete.
 */
export const pruneQuoteItemSelection = <T extends SelectableQuoteItem>(
    selection: ReadonlySet<string>,
    items: readonly T[],
): Set<string> => {
    const alive = new Set(items.map((it) => it.id));
    return new Set([...selection].filter((id) => alive.has(id)));
};

/**
 * Counters for the bulk action bar. Only selected ids that still exist count,
 * so a stale selection can never claim more positions than there are.
 */
export const quoteSelectionStats = <T extends SelectableQuoteItem>(
    items: readonly T[],
    selection: ReadonlySet<string> = emptySelection,
): QuoteSelectionStats => {
    let total = 0;
    let excluded = 0;
    for (const it of items) {
        if (!selection.has(it.id)) continue;
        total += 1;
        if (isQuoteItemExcluded(it)) excluded += 1;
    }
    return { total, excluded, active: total - excluded };
};

/** How many positions of the whole quote are struck (header hint). */
export const countExcludedQuoteItems = <T extends SelectableQuoteItem>(items: readonly T[]): number =>
    items.filter((it) => isQuoteItemExcluded(it)).length;

/**
 * Bulk switch of `excluded` for the given ids. Unaffected positions keep their
 * identity (same object reference), and un-striking writes `undefined` instead
 * of `false` so a never-struck position stays free of the flag in the jsonb
 * column.
 */
export const setQuoteItemsExcluded = <T extends SelectableQuoteItem>(
    items: readonly T[],
    ids: ReadonlySet<string>,
    excluded: boolean,
): T[] =>
    items.map((it) => {
        if (!ids.has(it.id) || isQuoteItemExcluded(it) === excluded) return it;
        return { ...it, excluded: excluded ? true : undefined };
    });

/**
 * Re-import guard (LV from a tender): recognition keys of the positions that
 * are already in the quote AND struck. A struck position stays struck — it
 * keeps counting as present, so the same LV line is neither silently healed
 * nor added a second time.
 */
export const excludedQuoteItemKeys = <T extends SelectableQuoteItem>(
    items: readonly T[],
    // NoInfer: the element type comes from `items` alone. Otherwise a key
    // function written against a narrower shape (quoteItemLvKey) would pull the
    // whole list down to that shape.
    keyOf: (item: NoInfer<T>) => string,
): Set<string> => {
    const out = new Set<string>();
    for (const it of items) {
        if (!isQuoteItemExcluded(it)) continue;
        const key = keyOf(it);
        if (key) out.add(key);
    }
    return out;
};

/** Import rows split into the ones to take over and the ones a strike blocks. */
export interface ExcludedDuplicateSplit<R> {
    accepted: R[];
    skipped: R[];
}

export const splitExcludedDuplicates = <R>(
    rows: readonly R[],
    keyOf: (row: NoInfer<R>) => string,
    excludedKeys: ReadonlySet<string>,
): ExcludedDuplicateSplit<R> => {
    const accepted: R[] = [];
    const skipped: R[] = [];
    for (const row of rows) {
        const key = keyOf(row);
        if (key && excludedKeys.has(key)) skipped.push(row);
        else accepted.push(row);
    }
    return { accepted, skipped };
};
